import { useTranslation } from "react-i18next";
import {
  Address,
  Defect,
  DefectType,
  ImageList,
  PunchlistText,
  StatusEditor,
} from "../..";
import { useEffect, useMemo, useState } from "react";
import { UserRole } from "imagine-users";
import {
  Alert,
  Button,
  ImageDropzone,
  Label,
  Select,
  SelectOption,
  TextInput,
} from "imagine-ui";
import { Api } from "imagine-essentials";
import { Image } from "../..";

interface Props {
  defect: Defect;
  address?: Address;
  onSave: (defect: Defect, images: File[], removedImage: Image[]) => void;
  onChange?: (hasChanges: boolean) => void;
  mode: "create" | "edit";
  userRole: UserRole;
  errorMessage?: string;
  successMessage?: string;
  warningMessage?: string;
  addressId?: number;
  projectOptions?: SelectOption[];
  addressOptions?: SelectOption[];
  canRemoveImages?: boolean;
  showType?: boolean;
  saving?: boolean;
}

export const DefectEditor = (props: Props) => {
  const { t } = useTranslation();

  const [defect, setDefect] = useState(props.defect);
  const [initialDefect, setInitialDefect] = useState<Defect>(props.defect);
  const [images, setImages] = useState<File[]>([]);
  const [removedImages, setRemovedImages] = useState<Image[]>([]);

  const updateDefect = (update: Partial<Defect>) => {
    const updatedDefect = { ...defect, ...update };
    setDefect(updatedDefect);
  };

  const removeImage = (image: Image) => {
    if (!defect.images) return;
    const newImages = defect.images.filter((i) => i !== image);
    setDefect({ ...defect, images: newImages });
    setRemovedImages([...removedImages, image]);
  };

  const save = () => {
    props.onSave(defect, images, removedImages);
    setInitialDefect(defect);
    setImages([]); // Clear images after saving
    setRemovedImages([]); // Clear removed images after saving
  };

  useEffect(() => {
    setDefect(props.defect);
  }, [props.defect]);

  useEffect(() => {
    if (props.onChange) {
      let hasChanges = false;
      if (defect.title !== initialDefect.title) hasChanges = true;
      else if (defect.description !== initialDefect.description)
        hasChanges = true;
      else if (defect.type !== initialDefect.type) hasChanges = true;
      else if (images.length > 0) hasChanges = true;
      else if (removedImages.length > 0) hasChanges = true;
      props.onChange(hasChanges);
    }
  }, [images, removedImages, props.onChange, defect]);

  const editorTitle =
    props.mode === "edit"
      ? t("punchlist:editDefect")
      : t("punchlist:createDefect");

  const defectTypeOptions = [
    {
      value: DefectType.DEFAULT,
      label: PunchlistText.getDefectTypeText(DefectType.DEFAULT),
    },
    {
      value: DefectType.PRE_REGISTRATION,
      label: PunchlistText.getDefectTypeText(DefectType.PRE_REGISTRATION),
    },
    {
      value: DefectType.INSPECTION,
      label: PunchlistText.getDefectTypeText(DefectType.INSPECTION),
    },
  ];

  const uploadLimit = useMemo(() => {
    if (props.defect.images) {
      return 8 - props.defect.images.length;
    }
    return 8;
  }, [props.defect.images]);

  return (
    <div>
      <h2>{editorTitle}</h2>
      {props.successMessage && (
        <Alert color="success" className="mb">
          {props.successMessage}
        </Alert>
      )}
      {props.warningMessage && (
        <Alert color="warning" className="mb">
          {props.warningMessage}
        </Alert>
      )}
      {props.errorMessage && (
        <Alert color="danger" className="mb">
          {props.errorMessage}
        </Alert>
      )}

      <div className="grid columns-2 columns-1--md gap">
        {props.address && (
          <div>
            <h6>
              {props.address.projectName}, {props.address.name}
            </h6>
          </div>
        )}
        <div className="colspan-2 colspan-1--md">
          <Label>{t("punchlist:shortDefectDescription")}</Label>
          <TextInput
            value={defect.title}
            onChange={(val) => updateDefect({ title: val })}
            maxLength={50}
            showSignsLeft
          />
        </div>
        {props.showType && (
          <div className="colspan-2 colspan-1--md">
            <Label>{t("imagine:type")}</Label>
            <div className="w-max-300">
              <Select
                options={defectTypeOptions}
                selected={defect.type}
                onSelectedNumber={(val) => updateDefect({ type: val })}
              />
            </div>
          </div>
        )}
        <div className="colspan-2 colspan-1--md">
          <Label>{t("punchlist:detailedDescription")}</Label>
          <TextInput
            value={defect.description}
            onChange={(val) => updateDefect({ description: val })}
            multiline
            height="md"
          />
        </div>
        <div className="colspan-2 colspan-1--md">
          <Label>{t("imagine:images")}</Label>
          {defect.images && (
            <ImageList
              images={defect.images}
              onRemove={removeImage}
              url={Api.getHost() + "/upload/defect-images/"}
              className="mb-sm"
              readonly={!props.canRemoveImages}
            />
          )}
          <ImageDropzone
            onChange={setImages}
            key={props.mode}
            limit={uploadLimit}
          />
          {images.length > 0 && <p>{t("punchlist:imagesUploadedOnSave")}</p>}
        </div>
        {/* {props.mode === "create" && (
          <div className="colspan-2 colspan-1--md">
            <Label>{t("imagine:status")}</Label>
            <StatusEditor
              defect={defect}
              userRole={props.userRole}
              onChange={(val) => updateDefect(val)}
              mode="select"
            />
          </div>
        )} */}
        <div className="colspan-2 colspan-1--md">
          <Button onClick={save} loading={props.saving}>
            {t("imagine:save")}
          </Button>
        </div>
      </div>
    </div>
  );
};
